import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Loader2, ArrowLeft, Save, Trash2, ChevronUp, ChevronDown, GripVertical, Layers, GitBranch, AlertTriangle, Sparkles, Zap, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AiDocBuilderDialog } from "@/components/AiDocBuilderDialog";
import { AiSettingsDialog } from "@/components/AiSettingsDialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";
import { withUniqueFieldKeys, slug } from "@/lib/fieldKeys";

const CRIMSON = "var(--brand)";
const FIELD_TYPES = ["text", "textarea", "number", "date", "select", "checkbox", "table", "image", "content"];
const COND_OPS = [
  { v: "eq", l: "is equal to" },
  { v: "ne", l: "is not equal to" },
  { v: "in", l: "is any of" },
  { v: "filled", l: "is answered / ticked" },
  { v: "empty", l: "is blank / unticked" },
];
const SIGNING = [
  { v: "both", l: "Both parties sign (participant/guardian + office)" },
  { v: "office", l: "Office signs only" },
  { v: "none", l: "No signatures required" },
];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function DocumentBuilder() {
  const { can } = useAuth();
  const [list, setList] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // template object or null
  const [saving, setSaving] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [logicOpen, setLogicOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [aiTemplate, setAiTemplate] = useState(null); // set => AI dialog works on this existing form

  const load = () => Promise.all([api.docTemplates(), api.docCategories()])
    .then(([t, c]) => { setList(t); setCategories(c); })
    .catch(() => { setList([]); toast.error("Could not load templates."); });
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ title: "", description: "", signing_mode: "both", status: "Active", category_id: categories[0]?.id || null, sections: [] });
  const openEdit = async (id) => { try { setEditing(await api.docTemplate(id)); } catch { toast.error("Could not open template."); } };
  const deleteTemplate = async (t, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete the document type "${t.title}"? You can only do this once all documents created from it have been deleted.`)) return;
    try {
      await api.deleteDocTemplate(t.id);
      toast.success(`"${t.title}" deleted.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed."); }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Give the document a title."); return; }
    // ensure every field has a unique key (also repairs templates saved with duplicates)
    const clean = { ...editing, sections: withUniqueFieldKeys(editing.sections) };
    setSaving(true);
    try {
      if (editing.id) await api.updateDocTemplate(editing.id, clean);
      else await api.createDocTemplate(clean);
      toast.success("Template saved.");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  // section/field mutators
  const upd = (fn) => setEditing((e) => { const c = structuredClone(e); fn(c); return c; });
  const addSection = () => upd((c) => c.sections.push({ id: uid(), title: "New section", description: "", fields: [] }));
  const setSection = (si, k, v) => upd((c) => { c.sections[si][k] = v; });
  const removeSection = (si) => upd((c) => c.sections.splice(si, 1));
  const moveSection = (si, d) => upd((c) => { const t = c.sections[si + d]; if (!t) return; c.sections[si + d] = c.sections[si]; c.sections[si] = t; });
  const addField = (si) => upd((c) => c.sections[si].fields.push({ id: uid(), key: "", label: "New field", type: "text", options: [], required: false, help: "", width: "full", subfields: [] }));
  const setField = (si, fi, k, v) => upd((c) => { c.sections[si].fields[fi][k] = v; });
  const removeField = (si, fi) => upd((c) => c.sections[si].fields.splice(fi, 1));
  const moveField = (si, fi, d) => upd((c) => { const arr = c.sections[si].fields; const t = arr[fi + d]; if (!t) return; arr[fi + d] = arr[fi]; arr[fi] = t; });
  const setSub = (si, fi, subs) => upd((c) => { c.sections[si].fields[fi].subfields = subs; });

  if (list === null) return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: CRIMSON }} /></div>;

  // Flat list of all fields (for conditional-visibility "controlling field" pickers).
  const allFields = (editing?.sections || []).flatMap((s) =>
    (s.fields || []).filter((f) => f.type !== "table" && f.type !== "content" && f.type !== "image")
      .map((f) => ({ id: f.id, key: f.key || slug(f.label), label: f.label, type: f.type, options: f.options || [] })));

  // Table fields (+ their column keys) available for automation mapping.
  const allTables = (editing?.sections || []).flatMap((s) =>
    (s.fields || []).filter((f) => f.type === "table")
      .map((f) => ({ key: f.key || slug(f.label), label: f.label,
                     columns: (f.subfields || []).map((sf) => sf.key || slug(sf.label)) })));

  // Top-down flow check: a condition should only reference fields that appear ABOVE the
  // dependent field/section. posMap gives each field a global ordinal (section*1000 + field).
  const posMap = {};
  (editing?.sections || []).forEach((s, si) => (s.fields || []).forEach((f, fi) => {
    if (f.type !== "table") posMap[f.key || slug(f.label)] = si * 1000 + fi;
  }));
  const belowLabels = (cfg, ownPos) => {
    const rules = Array.isArray(cfg?.rules) ? cfg.rules : (cfg?.field ? [cfg] : []);
    return rules.filter((r) => r?.field && posMap[r.field] !== undefined && posMap[r.field] > ownPos)
      .map((r) => allFields.find((x) => x.key === r.field)?.label || r.field);
  };

  // Read-only audit list of every conditional section/field (with any below-reference warning).
  const logicEntries = (editing?.sections || []).flatMap((s, si) => {
    const rows = [];
    if (s.visible_when) rows.push({ type: "Section", section: s.title, label: s.title, summary: condSummary(s.visible_when, allFields), warn: belowLabels(s.visible_when, si * 1000 - 0.5) });
    (s.fields || []).forEach((f, fi) => { if (f.visible_when) rows.push({ type: "Field", section: s.title, label: f.label, summary: condSummary(f.visible_when, allFields), warn: belowLabels(f.visible_when, si * 1000 + fi) }); });
    return rows;
  });

  // -------- editor --------
  if (editing) {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="template-editor">
        <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-3.5 w-3.5" /> All documents</button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold font-brand text-slate-800">{editing.id ? "Edit document" : "New document"}</h1>
          <div className="flex items-center gap-2">
            {can.has("settings.document_builder") && <Button variant="outline" onClick={() => { setAiTemplate(editing); setAiOpen(true); }} data-testid="ask-ai-btn"><Sparkles className="h-4 w-4 mr-1.5" style={{ color: CRIMSON }} />Ask AI</Button>}
            <Button variant="outline" onClick={() => setLogicOpen(true)} data-testid="logic-map-btn"><GitBranch className="h-4 w-4 mr-1.5" />Logic map{logicEntries.length ? ` (${logicEntries.length})` : ""}</Button>
            <Button onClick={save} disabled={saving} style={{ backgroundColor: CRIMSON }} data-testid="template-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3 mb-4">
          <div className="sm:col-span-2">
            <Label className="text-[11px] font-semibold text-slate-600">Document title *</Label>
            <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1 h-9 text-sm" data-testid="template-title" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[11px] font-semibold text-slate-600">Description</Label>
            <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 text-sm min-h-[44px]" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Tab / category</Label>
            <Select value={editing.category_id || ""} onValueChange={(v) => setEditing({ ...editing, category_id: v })}>
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="template-category"><SelectValue placeholder="Select tab…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id} className="text-sm">{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Signing mode</Label>
            <Select value={editing.signing_mode} onValueChange={(v) => setEditing({ ...editing, signing_mode: v })}>
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="template-signing"><SelectValue /></SelectTrigger>
              <SelectContent>{SIGNING.map((s) => <SelectItem key={s.v} value={s.v} className="text-sm">{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Status</Label>
            <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
              <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{["Active", "Archived"].map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-2.5 cursor-pointer">
              <input type="checkbox" checked={!!editing.visible_to_workers} onChange={(e) => setEditing({ ...editing, visible_to_workers: e.target.checked })} className="accent-brand mt-0.5" data-testid="template-visible-workers" />
              <span className="text-[11px] text-slate-600">
                <span className="font-semibold text-slate-700">Visible to Support Workers</span><br />
                When ticked, support workers can open the <strong>current signed</strong> version of this document (Support Worker PDF) for their participants. They never see draft, awaiting or archived versions.
              </span>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          {(editing.sections || []).map((s, si) => (
            <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`section-${si}`}>
              <div className="flex items-center gap-2 mb-2">
                <GripVertical className="h-4 w-4 text-slate-300" />
                <Input value={s.title} onChange={(e) => setSection(si, "title", e.target.value)} className="h-8 text-sm font-semibold flex-1" data-testid={`section-title-${si}`} />
                <button onClick={() => moveSection(si, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => moveSection(si, 1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => removeSection(si)} className="text-slate-400 hover:text-rose-600" data-testid={`section-remove-${si}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input value={s.description} onChange={(e) => setSection(si, "description", e.target.value)} placeholder="Section description (optional)" className="h-8 text-xs mb-2" />
              {s.visible_when && <div className="mb-2"><Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] py-0 font-normal whitespace-normal text-left" data-testid={`section-cond-badge-${si}`}>{condSummary(s.visible_when, allFields)}</Badge></div>}
              {s.visible_when && belowLabels(s.visible_when, si * 1000 - 0.5).length > 0 && (
                <div className="mb-2 flex items-start gap-1.5 text-[10px] text-rose-600" data-testid={`section-cond-warn-${si}`}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                  <span>This section depends on <strong>{belowLabels(s.visible_when, si * 1000 - 0.5).join(", ")}</strong>, which appear below it. Answers flow top-down — move the controlling field(s) above this section.</span>
                </div>
              )}
              <label className="flex items-center gap-1 text-[11px] text-slate-600 mb-2"><input type="checkbox" checked={!!s.visible_when} onChange={(e) => setSection(si, "visible_when", e.target.checked ? { match: "all", rules: [{ field: "", op: "eq", value: "" }] } : null)} className="accent-brand" data-testid={`section-cond-toggle-${si}`} /> Only show this section conditionally</label>
              <label className="flex items-center gap-1 text-[11px] text-slate-600 mb-2 ml-4" title="Hide this whole section from the Support Worker PDF"><input type="checkbox" checked={!!s.worker_hidden} onChange={(e) => setSection(si, "worker_hidden", e.target.checked)} className="accent-brand" data-testid={`section-worker-hidden-${si}`} /> Hide from Support Worker copy</label>
              {s.visible_when && (
                <div className="rounded border border-amber-200 bg-amber-50/60 p-2 mb-3" data-testid={`section-cond-${si}`}>
                  <div className="text-[10px] font-semibold text-amber-700 mb-1.5">Only show this section when…</div>
                  <ConditionEditor cfg={s.visible_when} allFields={allFields} onChange={(v) => setSection(si, "visible_when", v)} testPrefix={`section-cond-${si}`} />
                </div>
              )}
              <div className="space-y-2">
                {(s.fields || []).map((f, fi) => (
                  <FieldRow key={f.id} f={f} si={si} fi={fi} allFields={allFields.filter((x) => x.id !== f.id)}
                    warn={f.visible_when ? belowLabels(f.visible_when, si * 1000 + fi) : []}
                    onChange={(k, v) => setField(si, fi, k, v)} onRemove={() => removeField(si, fi)}
                    onMove={(d) => moveField(si, fi, d)} onSub={(subs) => setSub(si, fi, subs)} />
                ))}
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs mt-2" onClick={() => addField(si)} data-testid={`add-field-${si}`}><Plus className="h-3.5 w-3.5 mr-1" />Add field</Button>
            </div>
          ))}
          <Button variant="outline" onClick={addSection} data-testid="add-section"><Plus className="h-4 w-4 mr-1.5" />Add section</Button>
        </div>

        {editing.automations && (
          <AutomationsPanel automations={editing.automations} allTables={allTables}
            onChange={(a) => setEditing((e) => ({ ...e, automations: a }))} />
        )}

        <AiDocBuilderDialog open={aiOpen} onOpenChange={setAiOpen} template={aiTemplate}
          onBuilt={(tmpl) => setEditing({ ...tmpl, category_id: tmpl.category_id || categories[0]?.id || null })} />

        <Dialog open={logicOpen} onOpenChange={setLogicOpen}>
          <DialogContent data-testid="logic-map-dialog">
            <DialogHeader><DialogTitle className="text-base font-brand flex items-center gap-2"><GitBranch className="h-4 w-4" style={{ color: CRIMSON }} />Conditional logic map</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-1">Every section and field that only shows under a condition. Read-only — a quick way to audit the form's branching.</p>
            <div className="max-h-[60vh] overflow-auto mt-1 space-y-1.5">
              {logicEntries.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No conditional logic yet. Turn on "Conditional" on any field or section to add some.</div>}
              {logicEntries.map((e, i) => (
                <div key={i} className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-testid={`logic-entry-${i}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge className={`text-[9px] py-0 ${e.type === "Section" ? "bg-indigo-50 text-indigo-600 border-indigo-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{e.type}</Badge>
                    <span className="text-sm font-medium text-slate-800">{e.label}</span>
                    {e.type === "Field" && <span className="text-[10px] text-slate-400">in {e.section}</span>}
                    {e.warn.length > 0 && <Badge className="bg-rose-50 text-rose-600 border-rose-200 text-[9px] py-0"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" />below</Badge>}
                  </div>
                  <div className="text-[11px] text-amber-700">{e.summary}</div>
                  {e.warn.length > 0 && <div className="text-[10px] text-rose-600 mt-0.5">Depends on {e.warn.join(", ")}, placed below — move above to respect top-down flow.</div>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // -------- list --------
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="document-builder">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5"><FileText className="h-5 w-5" style={{ color: CRIMSON }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Documents</h1></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="manage-tabs"><Layers className="h-4 w-4 mr-1.5" />Manage tabs</Button>
          {can.has("settings.document_builder") && <Button variant="outline" onClick={() => setAiSettingsOpen(true)} data-testid="ai-settings-btn"><SlidersHorizontal className="h-4 w-4 mr-1.5" />AI Settings</Button>}
          {can.has("settings.document_builder") && <Button onClick={() => { setAiTemplate(null); setAiOpen(true); }} className="text-white" style={{ background: "linear-gradient(135deg,var(--brand),var(--brand-dark))" }} data-testid="ai-doc-builder-btn"><Sparkles className="h-4 w-4 mr-1.5" />AI Document Builder</Button>}
          <Button onClick={openNew} style={{ backgroundColor: CRIMSON }} data-testid="new-template"><Plus className="h-4 w-4 mr-1.5" />New document</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Build and customise care-plan documents (IRA, Support Plan, consent forms). Group them into tabs, add sections & fields, and choose the signing mode.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((t) => (
          <div key={t.id} role="button" tabIndex={0} onClick={() => openEdit(t.id)} data-testid={`template-card-${t.id}`}
            className="cursor-pointer text-left rounded-lg border border-slate-200 bg-white p-3.5 hover:border-brand hover:shadow-sm transition-all">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-sm text-slate-800">{t.title}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge className={`text-[9px] py-0 ${t.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{t.status}</Badge>
                {can.has("settings.document_builder") && (
                  <button onClick={(e) => deleteTemplate(t, e)} title="Delete document type (admin)"
                    className="text-slate-300 hover:text-rose-600 p-0.5" data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            {t.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
            <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-1.5">
              <Badge className="bg-rose-50 text-rose-600 border-rose-200 text-[9px] py-0">{categories.find((c) => c.id === t.category_id)?.name || "Uncategorised"}</Badge>
              {t.section_count} sections · {t.field_count} fields · {SIGNING.find((s) => s.v === t.signing_mode)?.v} signing
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-sm text-slate-400 col-span-full py-8 text-center">No documents yet. Create your first one.</div>}
      </div>
      <AiSettingsDialog open={aiSettingsOpen} onOpenChange={setAiSettingsOpen} />
      <CategoryManager open={catOpen} onOpenChange={setCatOpen} categories={categories} onChanged={load} />
      <AiDocBuilderDialog open={aiOpen} onOpenChange={setAiOpen} template={aiTemplate}
        onBuilt={(tmpl) => setEditing({ ...tmpl, category_id: tmpl.category_id || categories[0]?.id || null })} />
    </div>
  );
}

function AutomationsPanel({ automations, allTables, onChange }) {
  const ap = automations || {};
  const writebacks = ap.flag_writebacks || [];
  const autopop = ap.autopopulate || [];
  const tableKeys = allTables.map((t) => t.key);

  const setWb = (i, k, v) => {
    const next = structuredClone(ap);
    next.flag_writebacks = next.flag_writebacks || [];
    next.flag_writebacks[i] = { ...next.flag_writebacks[i], [k]: v };
    onChange(next);
  };
  const setAp = (i, k, v) => {
    const next = structuredClone(ap);
    next.autopopulate = next.autopopulate || [];
    next.autopopulate[i] = { ...next.autopopulate[i], [k]: v };
    onChange(next);
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 mt-4" data-testid="automations-panel">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="h-4 w-4 text-indigo-600" />
        <h3 className="text-sm font-semibold text-slate-800">Automations</h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">Behaviour that runs automatically — no code needed. Edit the mappings below to point these at any tables/fields.</p>

      {/* Auto-populate */}
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-indigo-700 mb-1.5 flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" />Auto-populate from the participant (on new form)</div>
        <div className="space-y-2">
          {autopop.length === 0 && <div className="text-[11px] text-slate-400">None configured.</div>}
          {autopop.map((r, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-white p-2.5" data-testid={`automation-autopop-${i}`}>
              <div>
                <Label className="text-[10px] text-slate-500">Form table</Label>
                <Select value={r.table || ""} onValueChange={(v) => setAp(i, "table", v)}>
                  <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid={`autopop-table-${i}`}><SelectValue placeholder="Pick a table…" /></SelectTrigger>
                  <SelectContent>{tableKeys.map((k) => <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Participant child table</Label>
                <Input value={r.from_participant_table || ""} onChange={(e) => setAp(i, "from_participant_table", e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`autopop-src-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Flag write-backs */}
      <div>
        <div className="text-[11px] font-semibold text-indigo-700 mb-1.5 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Flag "on file" when signed (on approval)</div>
        <div className="space-y-2">
          {writebacks.length === 0 && <div className="text-[11px] text-slate-400">None configured.</div>}
          {writebacks.map((r, i) => (
            <div key={i} className="rounded-md border border-slate-200 bg-white p-2.5" data-testid={`automation-writeback-${i}`}>
              <label className="flex items-center gap-2 mb-2">
                <input type="checkbox" checked={!!r.enabled} onChange={(e) => setWb(i, "enabled", e.target.checked)} className="accent-indigo-600" data-testid={`writeback-enabled-${i}`} />
                <Input value={r.label || ""} onChange={(e) => setWb(i, "label", e.target.value)} className="h-7 text-xs font-medium flex-1" placeholder="Rule name" data-testid={`writeback-label-${i}`} />
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Form table</Label>
                  <Select value={r.source_table || ""} onValueChange={(v) => setWb(i, "source_table", v)}>
                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid={`writeback-source-${i}`}><SelectValue placeholder="Table…" /></SelectTrigger>
                    <SelectContent>{tableKeys.map((k) => <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] text-slate-500">Link column</Label><Input value={r.link_field || ""} onChange={(e) => setWb(i, "link_field", e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`writeback-link-${i}`} /></div>
                <div><Label className="text-[10px] text-slate-500">Participant table</Label><Input value={r.parent_table || ""} onChange={(e) => setWb(i, "parent_table", e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`writeback-parent-${i}`} /></div>
                <div><Label className="text-[10px] text-slate-500">Match field</Label><Input value={r.match_field || ""} onChange={(e) => setWb(i, "match_field", e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`writeback-match-${i}`} /></div>
                <div><Label className="text-[10px] text-slate-500">Flag field</Label><Input value={r.flag_field || ""} onChange={(e) => setWb(i, "flag_field", e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`writeback-flag-${i}`} /></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoryManager({ open, onOpenChange, categories, onChanged }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { await api.createDocCategory({ name: newName.trim(), order: categories.length }); setNewName(""); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); }
  };
  const rename = async (c, name) => { try { await api.updateDocCategory(c.id, { name, order: c.order }); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };
  const del = async (c) => { try { await api.deleteDocCategory(c.id); toast.success("Tab deleted."); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-manager">
        <DialogHeader><DialogTitle className="text-base font-brand flex items-center gap-2"><Layers className="h-4 w-4" style={{ color: CRIMSON }} />Document tabs</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Tabs group your documents (e.g. Care Plans, Consents). They appear on each participant's profile. At least one is always required.</p>
        <div className="space-y-2 mt-1">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2" data-testid={`cat-${c.id}`}>
              <Input defaultValue={c.name} onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && rename(c, e.target.value.trim())} className="h-8 text-sm flex-1" data-testid={`cat-name-${c.id}`} />
              <button onClick={() => del(c)} disabled={categories.length <= 1} className="text-slate-400 hover:text-rose-600 disabled:opacity-30" data-testid={`cat-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New tab name…" className="h-8 text-sm flex-1" data-testid="cat-new-name" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button size="sm" onClick={add} disabled={busy || !newName.trim()} style={{ backgroundColor: CRIMSON }} className="h-8" data-testid="cat-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function condSummary(cfg, allFields) {
  const rules = Array.isArray(cfg?.rules) ? cfg.rules : (cfg?.field ? [cfg] : []);
  const active = rules.filter((r) => r && r.field);
  if (!active.length) return "Shows when a condition is set…";
  const label = (key) => (allFields || []).find((x) => x.key === key)?.label || key;
  const OP = { eq: "is", ne: "is not", in: "is any of", filled: "is answered", empty: "is blank" };
  const parts = active.map((r) => {
    const base = `${label(r.field)} ${OP[r.op] || "is"}`;
    return r.op === "filled" || r.op === "empty" ? base : `${base} “${r.value || "…"}”`;
  });
  const joiner = (cfg?.match || "all") === "any" ? " OR " : " AND ";
  return "Shows when " + parts.join(joiner);
}

function ConditionEditor({ cfg, allFields, onChange, testPrefix }) {
  const rules = Array.isArray(cfg?.rules) ? cfg.rules
    : (cfg?.field ? [{ field: cfg.field, op: cfg.op || "eq", value: cfg.value || "" }] : [{ field: "", op: "eq", value: "" }]);
  const match = cfg?.match || "all";
  const commit = (nextRules, nextMatch = match) => onChange({ match: nextMatch, rules: nextRules });
  const setRule = (i, patch) => commit(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRule = () => commit([...rules, { field: "", op: "eq", value: "" }]);
  const delRule = (i) => commit(rules.filter((_, j) => j !== i));

  return (
    <div className="space-y-2">
      {rules.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">Match</span>
          <Select value={match} onValueChange={(v) => commit(rules, v)}>
            <SelectTrigger className="h-7 text-[11px] w-44" data-testid={`${testPrefix}-match`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">ALL conditions (AND)</SelectItem>
              <SelectItem value="any" className="text-sm">ANY condition (OR)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {rules.map((r, i) => {
        const ctrl = r.field ? (allFields || []).find((x) => x.key === r.field) : null;
        const noVal = r.op === "filled" || r.op === "empty";
        return (
          <div key={i} className="grid sm:grid-cols-[1fr_130px_1fr_auto] gap-2 items-center" data-testid={`${testPrefix}-rule-${i}`}>
            <Select value={r.field || ""} onValueChange={(v) => setRule(i, { field: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`${testPrefix}-field-${i}`}><SelectValue placeholder="Choose a field…" /></SelectTrigger>
              <SelectContent>
                {(allFields || []).length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Add other fields first.</div>}
                {(allFields || []).map((x) => <SelectItem key={x.key} value={x.key} className="text-sm">{x.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={r.op || "eq"} onValueChange={(v) => setRule(i, { op: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`${testPrefix}-op-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{COND_OPS.map((o) => <SelectItem key={o.v} value={o.v} className="text-sm">{o.l}</SelectItem>)}</SelectContent>
            </Select>
            {noVal ? <div /> : ctrl?.type === "select" && r.op !== "in" ? (
              <Select value={r.value || ""} onValueChange={(v) => setRule(i, { value: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testPrefix}-value-${i}`}><SelectValue placeholder="Choose value…" /></SelectTrigger>
                <SelectContent>{(ctrl.options || []).map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : ctrl?.type === "checkbox" ? (
              <Select value={r.value || "true"} onValueChange={(v) => setRule(i, { value: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testPrefix}-value-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true" className="text-sm">Ticked (Yes)</SelectItem><SelectItem value="false" className="text-sm">Unticked (No)</SelectItem></SelectContent>
              </Select>
            ) : (
              <Input value={r.value || ""} onChange={(e) => setRule(i, { value: e.target.value })} placeholder={r.op === "in" ? "value1, value2" : "value to match"} className="h-8 text-xs" data-testid={`${testPrefix}-value-${i}`} />
            )}
            <button onClick={() => delRule(i)} disabled={rules.length <= 1} className="text-slate-400 hover:text-rose-600 disabled:opacity-30" data-testid={`${testPrefix}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        );
      })}
      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addRule} data-testid={`${testPrefix}-add`}><Plus className="h-3 w-3 mr-1" />Add condition</Button>
    </div>
  );
}

const PROFILE_FIELD_OPTIONS = [
  { value: "get_to_know_me", label: "Get to Know Me" },
  { value: "living_arrangements", label: "Living Arrangements" },
  { value: "hobbies", label: "Hobbies" },
  { value: "procedures_for_unsuccessful_contact_at_shift_start", label: "Procedures for Unsuccessful Contact at Shift Start" },
  { value: "communication_requirements", label: "Communication Requirements" },
  { value: "briefing_on_client", label: "Briefing on Client" },
  { value: "ndis_short_term_goals", label: "NDIS Short-Term Goals" },
  { value: "ndis_medium_to_long_term_goals", label: "NDIS Medium-to-Long-Term Goals" },
  { value: "emergency_instructions", label: "Emergency Instructions" },
  { value: "primary_address", label: "Primary Address (read-only)" },
  { value: "mobile_number", label: "Mobile Number" },
  { value: "phone_number", label: "Phone Number" },
  { value: "email", label: "Email" },
  { value: "ndis_number", label: "NDIS Number" },
  { value: "date_of_birth", label: "Date of Birth" },
  { value: "lives_alone", label: "Lives Alone" },
  { value: "cultural_background_and_ethnicity", label: "Cultural Background & Ethnicity" },
  { value: "religion", label: "Religion" },
  { value: "language", label: "Language" },
];

function FieldRow({ f, si, fi, allFields, warn = [], onChange, onRemove, onMove, onSub }) {
  const hasOptions = f.type === "select";
  const isTable = f.type === "table";
  const cond = f.visible_when || null;
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`field-${si}-${fi}`}>
      {cond && <div className="mb-2"><Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] py-0 font-normal whitespace-normal text-left" data-testid={`field-cond-badge-${si}-${fi}`}>{condSummary(cond, allFields)}</Badge></div>}
      {warn.length > 0 && (
        <div className="mb-2 flex items-start gap-1.5 text-[10px] text-rose-600" data-testid={`field-cond-warn-${si}-${fi}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>Depends on <strong>{warn.join(", ")}</strong>, which appear below this field. Answers flow top-down — move the controlling field(s) above it.</span>
        </div>
      )}
      <div className="grid sm:grid-cols-[1fr_130px_100px] gap-2 items-end">
        <div>
          <Label className="text-[10px] text-slate-500">Label</Label>
          <Input value={f.label} onChange={(e) => onChange("label", e.target.value)} className="h-8 text-sm" data-testid={`field-label-${si}-${fi}`} />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Type</Label>
          <Select value={f.type} onValueChange={(v) => onChange("type", v)}>
            <SelectTrigger className="h-8 text-xs" data-testid={`field-type-${si}-${fi}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t} className="text-sm capitalize">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Width</Label>
          <Select value={f.width} onValueChange={(v) => onChange("width", v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="full" className="text-sm">Full</SelectItem><SelectItem value="half" className="text-sm">Half</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      {hasOptions && (
        <div className="mt-2">
          <Label className="text-[10px] text-slate-500">Options (comma-separated)</Label>
          <Input value={(f.options || []).join(", ")} onChange={(e) => onChange("options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="h-8 text-xs" data-testid={`field-options-${si}-${fi}`} />
        </div>
      )}
      {f.type === "image" && (
        <div className="mt-2 rounded border border-slate-200 bg-white p-2">
          <div className="text-[10px] font-semibold text-slate-500 mb-1.5">Display image (shown read-only in the form &amp; on the PDF)</div>
          {f.image_url && <img src={f.image_url} alt="preview" className="max-h-40 rounded border border-slate-100 mb-2" data-testid={`field-image-preview-${si}-${fi}`} />}
          <input type="file" accept="image/*" className="text-[11px]" data-testid={`field-image-upload-${si}-${fi}`}
            onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              try { const r = await api.uploadDocImage(file); onChange("image_url", r.data_url); toast.success("Image added."); }
              catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); }
            }} />
        </div>
      )}
      {f.type === "content" && (
        <div className="mt-2 rounded border border-slate-200 bg-white p-2" data-testid={`field-content-editor-${si}-${fi}`}>
          <div className="text-[10px] font-semibold text-slate-500 mb-1.5">Display text (rich text — shown read-only in the form &amp; on the PDF). Use headings and bold to structure consent / instruction wording.</div>
          <RichTextEditor value={f.content || ""} onChange={(html) => onChange("content", html)} headings testid={`field-content-${si}-${fi}`} placeholder="Type the consent / instruction text…" />
        </div>
      )}
      {isTable && (
        <div className="mt-2 rounded border border-slate-200 bg-white p-2">
          <div className="text-[10px] font-semibold text-slate-500 mb-1.5">Table columns</div>
          {(f.subfields || []).map((sf, k) => {
            const setSubAt = (patch) => { const subs = [...f.subfields]; subs[k] = { ...sf, ...patch }; onSub(subs); };
            return (
              <div key={sf.id || k} className="rounded border border-slate-100 bg-slate-50/60 p-1.5 mb-1.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  {(() => {
                    const thisKey = slug(sf.label);
                    const isDupe = thisKey && (f.subfields || []).some((x, j) => j !== k && slug(x.label) === thisKey);
                    return (
                      <>
                        <Input value={sf.label} onChange={(e) => setSubAt({ label: e.target.value, key: slug(e.target.value) })} placeholder="Column label" className={`h-7 text-xs flex-1 ${isDupe ? "border-rose-400 focus-visible:ring-rose-400" : ""}`} data-testid={`col-label-${si}-${fi}-${k}`} />
                        {isDupe && <span className="text-[10px] text-rose-500 whitespace-nowrap" title="Two columns share the same key — rename one to avoid data conflicts">⚠ Duplicate key</span>}
                      </>
                    );
                  })()}
                  <Select value={sf.type || "text"} onValueChange={(v) => setSubAt({ type: v })}>
                    <SelectTrigger className="h-7 text-xs w-28" data-testid={`col-type-${si}-${fi}-${k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["text", "textarea", "number", "date", "select", "checkbox"].map((t) => <SelectItem key={t} value={t} className="text-sm capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => onSub(f.subfields.filter((_, j) => j !== k))} className="text-slate-400 hover:text-rose-600" data-testid={`col-del-${si}-${fi}-${k}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {sf.type === "select" && (
                  <div>
                    <Label className="text-[10px] text-slate-500">Dropdown options (comma-separated)</Label>
                    <Input value={(sf.options || []).join(", ")} onChange={(e) => setSubAt({ options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Option 1, Option 2, Option 3" className="h-7 text-xs" data-testid={`col-options-${si}-${fi}-${k}`} />
                  </div>
                )}
                <label className="flex items-center gap-1 text-[10px] text-slate-500"><input type="checkbox" checked={!!sf.visible_when} onChange={(e) => setSubAt({ visible_when: e.target.checked ? { match: "all", rules: [{ field: "", op: "eq", value: "" }] } : null })} className="accent-brand" data-testid={`col-cond-toggle-${si}-${fi}-${k}`} /> Only show this column conditionally</label>
                <label className="flex items-center gap-1 text-[10px] text-slate-500" title="Hide this column from the Support Worker PDF"><input type="checkbox" checked={!!sf.worker_hidden} onChange={(e) => setSubAt({ worker_hidden: e.target.checked })} className="accent-brand" data-testid={`col-worker-hidden-${si}-${fi}-${k}`} /> Hide from Support Worker copy</label>
                {sf.visible_when && (
                  <div className="rounded border border-amber-200 bg-amber-50/60 p-1.5" data-testid={`col-cond-${si}-${fi}-${k}`}>
                    <ConditionEditor cfg={sf.visible_when} allFields={allFields} onChange={(v) => setSubAt({ visible_when: v })} testPrefix={`col-cond-${si}-${fi}-${k}`} />
                  </div>
                )}
              </div>
            );
          })}
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onSub([...(f.subfields || []), { id: uid(), key: "", label: "", type: "text", options: [] }])} data-testid={`col-add-${si}-${fi}`}><Plus className="h-3 w-3 mr-1" />Add column</Button>
        </div>
      )}
      <div className="flex items-center gap-3 mt-2">
        <label className="flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={!!f.required} onChange={(e) => onChange("required", e.target.checked)} className="accent-brand" data-testid={`field-required-${si}-${fi}`} /> Required</label>
        <label className="flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={!!cond} onChange={(e) => onChange("visible_when", e.target.checked ? { field: "", op: "eq", value: "" } : null)} className="accent-brand" data-testid={`field-cond-toggle-${si}-${fi}`} /> Conditional</label>
        <label className="flex items-center gap-1 text-[11px] text-slate-600" title="Hide this question from the Support Worker PDF"><input type="checkbox" checked={!!f.worker_hidden} onChange={(e) => onChange("worker_hidden", e.target.checked)} className="accent-brand" data-testid={`field-worker-hidden-${si}-${fi}`} /> Hide from SW</label>
        <Input value={f.help} onChange={(e) => onChange("help", e.target.value)} placeholder="Help text (optional)" className="h-7 text-[11px] flex-1" />
        <button onClick={() => onMove(-1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="h-4 w-4" /></button>
        <button onClick={() => onMove(1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="h-4 w-4" /></button>
        <button onClick={onRemove} className="text-slate-400 hover:text-rose-600" data-testid={`field-remove-${si}-${fi}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {!isTable && f.type !== "content" && f.type !== "image" && (
        <div className="mt-2 flex items-center gap-2" data-testid={`field-profile-link-${si}-${fi}`}>
          <span className="text-[10px] text-slate-500 shrink-0 flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4m0 0L3 8m4-4 4 4"/><path d="M17 8v12m0 0 4-4m-4 4-4-4"/></svg>
            Sync with participant profile field:
          </span>
          <Select value={f.profile_field || "__none__"} onValueChange={(v) => onChange("profile_field", v === "__none__" ? null : v)}>
            <SelectTrigger className="h-7 text-[11px] w-64" data-testid={`field-profile-field-${si}-${fi}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" className="text-sm text-slate-400">Not linked</SelectItem>
              {PROFILE_FIELD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {f.profile_field && (
            f.profile_field === "primary_address"
              ? <span className="text-[10px] text-slate-500 font-medium">Pulled from the profile's primary address; read-only in the document</span>
              : <span className="text-[10px] text-emerald-600 font-medium">↕ 2-way sync in Draft</span>
          )}
        </div>
      )}
      {cond && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50/60 p-2" data-testid={`field-cond-${si}-${fi}`}>
          <div className="text-[10px] font-semibold text-amber-700 mb-1.5">Only show this field when…</div>
          <ConditionEditor cfg={cond} allFields={allFields} onChange={(v) => onChange("visible_when", v)} testPrefix={`field-cond-${si}-${fi}`} />
        </div>
      )}
    </div>
  );
}
